import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../lib/cn';

export interface ImageLightboxModalProps {
  open: boolean;
  onClose: () => void;
  photos: string[];
  initialIndex?: number;
  title?: string;
  subtitle?: string;
}

export function ImageLightboxModal({
  open,
  onClose,
  photos,
  initialIndex = 0,
  title,
  subtitle,
}: ImageLightboxModalProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  // Sync initialIndex when modal opens
  useEffect(() => {
    if (open) {
      setCurrentIndex(initialIndex);
    }
  }, [open, initialIndex]);

  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const hasMultiple = photos.length > 1;

  const handlePrev = useCallback(() => {
    if (!hasMultiple) return;
    setCurrentIndex((prev) => (prev - 1 + photos.length) % photos.length);
  }, [hasMultiple, photos.length]);

  const handleNext = useCallback(() => {
    if (!hasMultiple) return;
    setCurrentIndex((prev) => (prev + 1) % photos.length);
  }, [hasMultiple, photos.length]);

  // Keyboard navigation: Escape to close, Left/Right arrows for carousel
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft') {
        handlePrev();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose, handlePrev, handleNext]);

  if (!open || photos.length === 0) return null;

  const currentPhoto = photos[currentIndex] || photos[0];

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex flex-col items-center justify-between bg-black/90 backdrop-blur-md animate-fade-in select-none"
      onClick={onClose}
    >
      {/* Top Header Bar */}
      <div
        className="w-full flex items-center justify-between px-4 sm:px-6 py-4 text-white z-20 shrink-0 bg-gradient-to-b from-black/80 to-transparent"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="min-w-0 pr-4">
          {title && (
            <h3 className="text-sm sm:text-base font-bold text-white truncate max-w-xl">
              {title}
            </h3>
          )}
          <div className="flex items-center gap-2 mt-0.5">
            {subtitle && (
              <span className="text-xs text-white/70 truncate">{subtitle}</span>
            )}
            {hasMultiple && (
              <span className="text-xs font-semibold bg-white/20 px-2 py-0.5 rounded-full text-white/90">
                {currentIndex + 1} / {photos.length}
              </span>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="h-10 w-10 rounded-full bg-white/10 hover:bg-white/25 active:scale-95 text-white flex items-center justify-center transition-all cursor-pointer shrink-0"
          title="Close (Esc)"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Main Image Stage */}
      <div
        className="relative flex-1 w-full flex items-center justify-center px-4 py-2 min-h-0"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Previous Button (Only if multiple images) */}
        {hasMultiple && (
          <button
            type="button"
            onClick={handlePrev}
            className="absolute left-3 sm:left-6 z-20 h-11 w-11 sm:h-12 sm:w-12 rounded-full bg-black/60 hover:bg-black/90 active:scale-95 text-white border border-white/20 flex items-center justify-center transition-all shadow-xl hover:scale-105 cursor-pointer"
            title="Previous Photo (Left Arrow)"
            aria-label="Previous Photo"
          >
            <ChevronLeft className="h-6 w-6 sm:h-7 sm:w-7" />
          </button>
        )}

        {/* Enlarged Photo */}
        <div className="relative max-h-full max-w-full flex items-center justify-center p-2">
          <img
            key={currentPhoto}
            src={currentPhoto}
            alt={title || `Photo ${currentIndex + 1}`}
            className="max-h-[72vh] max-w-[92vw] sm:max-w-[85vw] object-contain rounded-lg shadow-2xl transition-all duration-200"
          />
        </div>

        {/* Next Button (Only if multiple images) */}
        {hasMultiple && (
          <button
            type="button"
            onClick={handleNext}
            className="absolute right-3 sm:right-6 z-20 h-11 w-11 sm:h-12 sm:w-12 rounded-full bg-black/60 hover:bg-black/90 active:scale-95 text-white border border-white/20 flex items-center justify-center transition-all shadow-xl hover:scale-105 cursor-pointer"
            title="Next Photo (Right Arrow)"
            aria-label="Next Photo"
          >
            <ChevronRight className="h-6 w-6 sm:h-7 sm:w-7" />
          </button>
        )}
      </div>

      {/* Bottom Thumbnail Strip (Only if multiple images) */}
      {hasMultiple && (
        <div
          className="w-full py-3 px-4 flex items-center justify-center gap-2 overflow-x-auto shrink-0 bg-gradient-to-t from-black/80 to-transparent z-20"
          onClick={(e) => e.stopPropagation()}
        >
          {photos.map((url, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => setCurrentIndex(idx)}
              className={cn(
                'relative h-14 w-14 shrink-0 rounded-lg overflow-hidden border-2 transition-all cursor-pointer bg-white/5',
                currentIndex === idx
                  ? 'border-primary ring-2 ring-primary/50 scale-105 opacity-100'
                  : 'border-white/20 opacity-60 hover:opacity-100 hover:border-white/50'
              )}
              title={`View Photo ${idx + 1}`}
            >
              <img src={url} alt={`Thumb ${idx + 1}`} className="h-full w-full object-cover" />
              {idx === 0 && (
                <span className="absolute bottom-0 inset-x-0 bg-primary/90 text-primary-foreground text-[8px] font-bold text-center py-0.2">
                  Cover
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>,
    document.body
  );
}
